import { useMutation, useQuery } from '@apollo/client';
import { useAccount, useLogin } from '@bnnvara/accounts';
import { track } from '@bnnvara/analytics/src/track';
import { useBrand } from '@bnnvara/brand';
import {
  CommentBox,
  CommentsComponent,
  sortNewestFirst,
  sortOldestFirst,
  sortRating,
} from '@bnnvara/comments';
import { string } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import deleteComment from '../../api/mutations/deleteCommentMutation.gql';
import addComment from '../../api/mutations/postCommentMutation.gql';
import reportComment from '../../api/mutations/reportCommentMutation.gql';
import voteComment from '../../api/mutations/voteCommentMutation.gql';
import fetchComments from '../../api/queries/comments.gql';
import useScrollToElement from '../../hooks/useScrollToElement';

const Comments = ({ contentId, contentType }) => {
  const showComments = true;
  const showCommentReport = true;
  const { slug: brandSlug } = useBrand();
  const brandName = brandSlug.charAt(0).toUpperCase() + brandSlug.slice(1);

  const reportUrl = process.browser ? window.location.href : '';

  const { isLoggedIn, logout, login } = useLogin();
  const { profile, token } = useAccount();

  const [isValidateMessageOpen, setValidateMessageOpen] = useState(false);
  const [isMessageSend, setMessageSend] = useState(false);
  const [orderDirection, setOrderDirection] = useState('DESC');
  const [orderField, setOrderField] = useState('createdAt');
  const [createOpinion, sendReport] = useMutation(reportComment);
  const [createVote] = useMutation(voteComment);

  const [removeComment, isCommentDeleted] = useMutation(deleteComment);
  const [isReportMessageSend, setReportMessageSend] = useState(false);

  const [createComment, sendResponse] = useMutation(addComment);

  const { loading, error, data } = useQuery(fetchComments, {
    variables: {
      contentId,
      activeUserId: profile.UID || null,
      orderField,
      orderDirection,
    },
  });

  const scrollToRef = useRef(null);
  // Don't scroll when SSR or there is no "reageren" hash in the url
  const skipScroll = !process.browser || document.location.hash !== '#reageren';

  useScrollToElement(scrollToRef, false, skipScroll);

  useEffect(() => {
    if (sendResponse.data && sendResponse.data.addComment) {
      setMessageSend(true);
      setValidateMessageOpen(false);
    }
    if (sendReport.data && sendReport.data.reportComment) {
      setReportMessageSend(true);
    }
  }, [sendReport.data, sendResponse]);

  const openLogin = (e) => {
    e.preventDefault();
    track('comments', 'click', 'login-registreer-comments');
    return login();
  };

  const [sortOption, setSortOption] = useState(sortNewestFirst);

  const selectOrder = (value) => {
    switch (value) {
      case sortOldestFirst:
        setOrderField('createdAt');
        setOrderDirection('ASC');
        setSortOption(sortOldestFirst);
        break;
      case sortRating:
        setOrderField('opinionPositiveCount');
        setOrderDirection('DESC');
        setSortOption(sortRating);
        break;
      default:
        setOrderField('createdAt');
        setOrderDirection('DESC');
        setSortOption(sortNewestFirst);
    }
  };

  const sendMutation = (getCommentValue, referenceId, commentType) => {
    if (commentType === 'comment') {
      const optimisticMainCommentObj = {
        id: referenceId,
        body: getCommentValue,
        contentId: Math.round(Math.random() * -1000000).toString(),
        createdAt: '2020-07-15T11:45:14+00:00',
        subComments: [],
        user: {
          username: profile.nickname,
          avatarUrl: profile.profilePicture || '',
          __typename: 'User',
        },
        isOptimistic: true,
        activeUser: {
          opinionPositive: false,
        },
      };
      createComment({
        variables: {
          contentId: referenceId,
          username: profile.nickname,
          userId: profile.UID,
          avatarUrl: profile.profilePicture || '',
          body: getCommentValue,
          contentType,
        },
        update: () => {
          try {
            data.comments.comments.unshift(optimisticMainCommentObj);
            // eslint-disable-next-line no-shadow
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
          }
        },
      });
    } else {
      const optimisticSubCommentObj = {
        id: referenceId,
        body: getCommentValue,
        contentId: Math.round(Math.random() * -1000000).toString(),
        createdAt: '2020-07-15T11:45:14+00:00',
        user: {
          username: profile.nickname,
          avatarUrl: profile.profilePicture || '',
          __typename: 'User',
        },
        isOptimistic: true,
        activeUser: {
          opinionPositive: false,
        },
      };
      createComment({
        variables: {
          contentId: referenceId,
          username: profile.nickname,
          userId: profile.UID,
          avatarUrl: profile.profilePicture || '',
          body: getCommentValue,
          contentType,
        },
        update: () => {
          try {
            data.comments.comments.map((item) => {
              if (item.id === referenceId) {
                return item.subComments.unshift(optimisticSubCommentObj);
              }
              return item;
            });
            // eslint-disable-next-line no-shadow
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
          }
        },
      });
    }

    track('comments', 'click', `${commentType}-post-comment`);
  };

  const sendReportMutation = (passedContentId, commentId, commentType) => {
    createOpinion({
      variables: {
        commentId,
        contentId: passedContentId,
        sourceBrandName: brandName,
        sourceUrl: reportUrl,
        username: profile.nickname,
        userId: profile.UID,
        avatarUrl: profile.profilePicture || '',
        type: 'inappropriate',
      },
    });

    track('comments', 'click', `${commentType}-report-comment`);
  };

  const sendVoteMutation = (
    passedContentId,
    commentId,
    voteType,
    commentType
  ) => {
    createVote({
      variables: {
        commentId,
        contentId: passedContentId,
        sourceBrandName: brandName,
        sourceUrl: reportUrl,
        username: profile.nickname,
        userId: profile.UID,
        avatarUrl: profile.profilePicture || '',
        type: 'positive',
      },
    });
    track('comments', 'click', `${voteType}-${commentType}`);
  };

  const sendDeleteCommentMutation = (commentId, jwtToken) => {
    removeComment({
      variables: {
        commentId,
        token: jwtToken,
      },
    });
  };

  if (loading || error) {
    return null;
  }
  if (!showComments || !data) {
    return null;
  }
  const { comments } = data;

  const getUserRole = (userProfile) => {
    const isUserModerator = (obj) => obj.value === 'moderator';
    if (
      userProfile.roles &&
      userProfile.roles.length > 0 &&
      userProfile.roles.some(isUserModerator)
    ) {
      return 'moderator';
    }
    return null;
  };

  const roles = getUserRole(profile);

  return (
    <>
      <span ref={scrollToRef} />
      <CommentBox
        isLoggedIn={isLoggedIn}
        profilePicture={profile.profilePicture}
        openLogin={openLogin}
        nickname={profile.nickname}
        logout={logout}
        isMessageOpen={isValidateMessageOpen}
        isMessageSend={isMessageSend}
        subComment={false}
        commentId={contentId}
        sendMutation={sendMutation}
      />
      {comments != null && comments.comments.length >= 1 && (
        <CommentsComponent
          key={contentId}
          data={comments}
          isLoggedIn={isLoggedIn}
          openLogin={openLogin}
          sendMutation={sendMutation}
          isReportMessageSend={isReportMessageSend}
          sendReportMutation={sendReportMutation}
          sendVoteMutation={sendVoteMutation}
          sendDeleteCommentMutation={sendDeleteCommentMutation}
          passedContentId={contentId}
          showCommentReport={showCommentReport}
          isMessageOpen={isValidateMessageOpen}
          isMessageSend={isMessageSend}
          jwtToken={token}
          commentId={contentId}
          roles={roles}
          isCommentDeleted={isCommentDeleted}
          selectOrder={selectOrder}
          popularOrder={orderDirection}
          sortOption={sortOption}
        />
      )}
    </>
  );
};

Comments.propTypes = {
  contentId: string.isRequired,
  contentType: string.isRequired,
};

export default Comments;
